import { useEffect, useMemo } from "react"
import { useRequestData } from "./useRequestData"
import { analyticsWrap } from "src/tags/analytics"
import { crashlyticsWrap } from "src/tags/crashlytics"
import { registerDevice } from "src/apis/auth/createDevice"
import { getUpdateDeviceFlag, setUpdateDeviceFlag } from "src/storage/updateDeviceFlag"
import { useFirebaseUserPropertiesData } from "src/hooks/useFirebasePropertiesData"
import { useSetRecoilState } from "recoil"
import { deviceTokenState } from "src/recoil/atoms/account/deviceTokenState"
import * as Notifications from "expo-notifications"
import { Platform } from "react-native"

export const useAccountIdEffect = () => {
  const requestDataResult = useRequestData()
  const { data: firebaseUserData, isLoading } = useFirebaseUserPropertiesData()
  const setDeviceToken = useSetRecoilState(deviceTokenState)
  const content = useMemo(() => {
    if (!requestDataResult.isOk) {
      return
    }
    return requestDataResult.content
  }, [requestDataResult])

  useEffect(() => {
    let isMounted = true
    let retryTimer: NodeJS.Timeout | null = null

    const cleanup = () => {
      isMounted = false
      if (retryTimer) {
        clearTimeout(retryTimer)
        retryTimer = null
      }
    }

    ;(async () => {
      if (content?.accountId) {
        if (content?.accessToken.length > 0) {
          getUpdateDeviceFlag().then(async (value) => {
            if (!value) {
              const result = await registerDevice(content?.accessToken)

              if (result.isOk) {
                setUpdateDeviceFlag()

                if (Platform.OS === "ios" || Platform.OS === "android") {
                  try {
                    const token = await Notifications.getDevicePushTokenAsync()
                    if (token?.data) {
                      setDeviceToken(token.data)
                    }
                  } catch (e) {
                    console.error("[useAccountIdEffect] Failed to store device token:", e)
                  }
                }
              } else {
                if (
                  !result.isOk &&
                  (result.error?.message?.includes("network") || result.error?.message?.includes("timeout"))
                ) {
                  retryTimer = setTimeout(async () => {
                    if (!isMounted) return

                    const retryResult = await registerDevice(content?.accessToken)
                    if (retryResult.isOk && isMounted) {
                      setUpdateDeviceFlag()

                      if (Platform.OS === "ios" || Platform.OS === "android") {
                        try {
                          const token = await Notifications.getDevicePushTokenAsync()
                          if (token?.data && isMounted) {
                            setDeviceToken(token.data)
                          }
                        } catch (e) {
                          console.error("[useAccountIdEffect] Failed to store device token on retry:", e)
                        }
                      }
                    }
                  }, 10000)
                }
              }
            } else {
              if (Platform.OS !== "web") {
                try {
                  const token = await Notifications.getDevicePushTokenAsync()
                  if (token?.data) {
                    setDeviceToken(token.data)
                  }
                } catch (e) {
                  console.error("[useAccountIdEffect] Failed to sync existing token:", e)
                }
              }
            }
          })
        }

        try {
          analyticsWrap.setUser(content?.accountId)
          crashlyticsWrap.setUser(content?.accountId)

          // TEMPORARY DISABLED: Firebase Analytics user properties update
          // Issue #2758 - Reducing excessive getAccountInfo API calls (185,306/day → 0/day)
          // This is causing ~$50/day AWS cost increase due to excessive API calls
          // TODO: Re-enable after implementing caching mechanism (Phase 2)
          // Expected timeline: 3-4 weeks
          /*
          if (!isLoading && firebaseUserData) {
            analyticsWrap.setUserProperties(firebaseUserData)
          }
          */
        } catch (e) {
          crashlyticsWrap.recordError(e, "accountIdSetError")
        }
      }
    })()

    return cleanup
  }, [content, isLoading, firebaseUserData, setDeviceToken])
}
